import type { ClipMediaMetadata } from "@/components/OmniCine/Timeline/EditorialMediaBinding";
import type { TimelineAddDetail } from "@/contracts/media/MediaPanelEvents";
import type { TimelineClip, TimelineSource } from "@/state/timeline/timeline.types";

export type TimelineAddClipDraft = Omit<TimelineClip, "kind"> & {
  kind?: TimelineAddDetail["kind"];
};

type TimelineAddClipBaseArgs = {
  startSec: number;
  endSec: number;
  durSec?: number;
  mediaId?: string | null;
  mediaLabel?: string | null;
  requestId?: string | null;
  source: TimelineSource;
  directUrl: string;
};

type BuildTimelineAddClipArgs = TimelineAddClipBaseArgs & {
  clipId: string;
  trackId: string;
  kind: NonNullable<TimelineAddDetail["kind"]>;
};

function withTimelineAddUrlFields<TTarget extends Record<string, unknown>>(
  target: TTarget,
  directUrl: string,
): TTarget {
  if (!directUrl) return target;

  return {
    ...target,
    url: directUrl,
    previewUrl: directUrl,
    mediaUrl: directUrl,
    src: directUrl,
  };
}

export function buildTimelineAddClip(args: BuildTimelineAddClipArgs): TimelineAddClipDraft {
  return withTimelineAddUrlFields({
    id: args.clipId,
    trackId: args.trackId,
    kind: args.kind,
    startSec: args.startSec,
    endSec: args.endSec,
    ...(args.mediaLabel ? { label: args.mediaLabel } : {}),
    ...(typeof args.durSec === "number" ? { durSec: args.durSec } : {}),
    ...(args.mediaId ? { mediaId: args.mediaId } : {}),
    ...(args.requestId ? { requestId: args.requestId } : {}),
    source: args.source,
  }, args.directUrl);
}

export function buildTimelineAddClipPatch(args: TimelineAddClipBaseArgs): Partial<TimelineAddClipDraft> {
  return withTimelineAddUrlFields({
    ...(typeof args.startSec === "number" ? { startSec: args.startSec } : {}),
    ...(typeof args.endSec === "number" ? { endSec: args.endSec } : {}),
    ...(typeof args.durSec === "number" ? { durSec: args.durSec } : {}),
    ...(args.mediaId ? { mediaId: args.mediaId } : {}),
    ...(args.mediaLabel ? { label: args.mediaLabel } : {}),
    ...(args.source ? { source: args.source } : {}),
    requestId: args.requestId ?? undefined,
  }, args.directUrl);
}

export function buildTimelineAddClipMediaMetadata(args: {
  clipId: string;
  detail: TimelineAddDetail;
  durSec?: number;
  directUrl: string;
  kind: NonNullable<TimelineAddDetail["kind"]>;
  mediaId?: string | null;
  requestId?: string | null;
  mediaLabel?: string | null;
}): ClipMediaMetadata {
  const { clipId, detail, durSec, directUrl, kind, mediaId, requestId, mediaLabel } = args;

  return {
    clipId,
    durationSec: typeof durSec === "number" ? durSec : undefined,
    mediaUrl: directUrl || undefined,
    kind: kind as ClipMediaMetadata["kind"],
    assetId: typeof detail.assetId === "string" ? detail.assetId : undefined,
    sourceLabel: typeof detail.sourceLabel === "string" ? detail.sourceLabel : mediaLabel ?? undefined,
    familyId: typeof detail.familyId === "string" ? detail.familyId : undefined,
    originType: detail.originType,
    hasPlayableAudio: typeof detail.hasPlayableAudio === "boolean" ? detail.hasPlayableAudio : undefined,
    frameRate: typeof detail.frameRate === "number" ? detail.frameRate : undefined,
    sampleRate: typeof detail.sampleRate === "number" ? detail.sampleRate : undefined,
    channelLayout: typeof detail.channelLayout === "string" ? detail.channelLayout : undefined,
    channelCount: typeof detail.channelCount === "number" ? detail.channelCount : undefined,
    audioBitDepth: typeof detail.audioBitDepth === "number" ? detail.audioBitDepth : undefined,
    audioCodec: typeof detail.audioCodec === "string" ? detail.audioCodec : undefined,
    sourceHash: typeof detail.sourceHash === "string" ? detail.sourceHash : undefined,
    containerFormat: typeof detail.containerFormat === "string" ? detail.containerFormat : undefined,
    schemaVersion: typeof detail.schemaVersion === "string" ? detail.schemaVersion : undefined,
    ingestId: typeof detail.ingestId === "string" ? detail.ingestId : undefined,
    sourceAssetId: typeof detail.sourceAssetId === "string" ? detail.sourceAssetId : mediaId ?? requestId ?? undefined,
    storagePath: typeof detail.storagePath === "string" ? detail.storagePath : undefined,
    fileName: typeof detail.fileName === "string" ? detail.fileName : undefined,
    sourceFileName: typeof detail.sourceFileName === "string" ? detail.sourceFileName : undefined,
    title: typeof detail.title === "string" ? detail.title : undefined,
    scene: typeof detail.scene === "string" ? detail.scene : undefined,
    take: typeof detail.take === "number" || typeof detail.take === "string" ? detail.take : undefined,
    camera: typeof detail.camera === "string" ? detail.camera : undefined,
    nativeWidth: typeof detail.nativeWidth === "number" ? detail.nativeWidth : undefined,
    nativeHeight: typeof detail.nativeHeight === "number" ? detail.nativeHeight : undefined,
    pixelAspectRatio: typeof detail.pixelAspectRatio === "number" ? detail.pixelAspectRatio : undefined,
    displayAspectRatio: typeof detail.displayAspectRatio === "number" ? detail.displayAspectRatio : undefined,
    colorSpace: typeof detail.colorSpace === "string" ? detail.colorSpace : undefined,
    transferFunction: typeof detail.transferFunction === "string" ? detail.transferFunction : undefined,
    hdrFormat: typeof detail.hdrFormat === "string" ? detail.hdrFormat : undefined,
    interlaced: typeof detail.interlaced === "boolean" ? detail.interlaced : undefined,
    videoBitDepth: typeof detail.videoBitDepth === "number" ? detail.videoBitDepth : undefined,
    sourceInSec: typeof detail.sourceInSec === "number" ? detail.sourceInSec : undefined,
    sourceOutSec: typeof detail.sourceOutSec === "number" ? detail.sourceOutSec : undefined,
    timecodeStart: typeof detail.timecodeStart === "string" ? detail.timecodeStart : undefined,
    dropFrame: typeof detail.dropFrame === "boolean" ? detail.dropFrame : undefined,
    reelName: typeof detail.reelName === "string" ? detail.reelName : undefined,
    streamLayout: typeof detail.streamLayout === "string" ? detail.streamLayout : undefined,
    importSource: typeof detail.importSource === "string" ? detail.importSource : undefined,
    ingestTimestamp: typeof detail.ingestTimestamp === "number" ? detail.ingestTimestamp : undefined,
    metadataHistory: Array.isArray(detail.metadataHistory) ? detail.metadataHistory : undefined,
    transcodeHistory: Array.isArray(detail.transcodeHistory) ? detail.transcodeHistory : undefined,
    offlineState: detail.offlineState,
    growingFile: typeof detail.growingFile === "boolean" ? detail.growingFile : undefined,
    analysisState: detail.analysisState,
    metadataRichnessScore: typeof detail.metadataRichnessScore === "number" ? detail.metadataRichnessScore : undefined,
    decodePerformanceScore: typeof detail.decodePerformanceScore === "number" ? detail.decodePerformanceScore : undefined,
    proxyStatus: detail.proxyStatus,
    preferredTimelineProxyId: typeof detail.preferredTimelineProxyId === "string" ? detail.preferredTimelineProxyId : undefined,
    preferredScrubProxyId: typeof detail.preferredScrubProxyId === "string" ? detail.preferredScrubProxyId : undefined,
    proxies: Array.isArray(detail.proxies) ? detail.proxies : undefined,
    waveformProxyUrl: typeof detail.waveformProxyUrl === "string" ? detail.waveformProxyUrl : undefined,
    previewProxyUrl: typeof detail.previewProxyUrl === "string" ? detail.previewProxyUrl : undefined,
    metadataVault: detail.metadataVault,
    sourceMetadata: detail.sourceMetadata,
    semanticMetadata: detail.semanticMetadata,
    userMetadata: detail.userMetadata,
    systemMetadata: detail.systemMetadata,
  };
}